import type { Cohort } from './types';

/**
 * Cohorts reference people by id, so the same person can appear in several
 * cohorts (Elon in PayPal Mafia, Tesla and Chaos CEOs) and — later — as
 * multiple eras of themselves.
 *
 * Tiers: A is a real team at a real moment, B is a themed all-star draw,
 * C is a chaos draw, historical is a snapshot of a whole year. Every tier
 * is drawn with the same probability today; the field is for later.
 */
export const COHORTS: Cohort[] = [
  // ── Tier A · real teams, real moments ───────────────────────────────────
  {
    id: 'paypal-2002',
    name: 'PAYPAL MAFIA',
    era: '2002',
    tier: 'A',
    accent: '#FF5C5C',
    members: [
      'elon-musk',
      'peter-thiel',
      'reid-hoffman',
      'max-levchin',
      'david-sacks',
      'keith-rabois',
      'roelof-botha',
    ],
  },
  {
    id: 'apple-2007',
    name: 'APPLE',
    era: 'IPHONE TEAM · 2007',
    tier: 'A',
    accent: '#E8E8ED',
    members: [
      'steve-jobs',
      'jony-ive',
      'tim-cook',
      'scott-forstall',
      'tony-fadell',
      'phil-schiller',
      'eddy-cue',
    ],
  },
  {
    id: 'facebook-2012',
    name: 'FACEBOOK',
    era: 'IPO · 2012',
    tier: 'A',
    accent: '#4C8DFF',
    members: [
      'mark-zuckerberg',
      'sheryl-sandberg',
      'chris-cox',
      'mike-schroepfer',
      'dustin-moskovitz',
      'andrew-bosworth',
    ],
  },
  {
    id: 'openai-2023',
    name: 'OPENAI',
    era: 'CHATGPT · 2023',
    tier: 'A',
    accent: '#5BE3B0',
    members: [
      'sam-altman',
      'ilya-sutskever',
      'greg-brockman',
      'mira-murati',
      'andrej-karpathy',
      'wojciech-zaremba',
    ],
  },
  {
    id: 'google-2004',
    name: 'GOOGLE',
    era: 'RISE OF SEARCH · 2004–08',
    tier: 'A',
    accent: '#FF5C5C',
    members: [
      'larry-page',
      'sergey-brin',
      'eric-schmidt',
      'marissa-mayer',
      'susan-wojcicki',
      'sundar-pichai',
    ],
  },
  {
    id: 'microsoft-early',
    name: 'MICROSOFT',
    era: 'EARLY EMPIRE · 1980s–90s',
    tier: 'A',
    accent: '#5BC8FF',
    members: ['bill-gates', 'paul-allen', 'steve-ballmer', 'charles-simonyi', 'ray-ozzie'],
  },
  {
    id: 'amazon-2000s',
    name: 'AMAZON',
    era: 'EVERYTHING STORE · 2000s',
    tier: 'A',
    accent: '#FFA94D',
    members: ['jeff-bezos', 'andy-jassy', 'werner-vogels', 'jeff-wilke', 'dave-limp'],
  },
  {
    id: 'tesla-2012',
    name: 'TESLA',
    era: 'MODEL S ERA · 2012',
    tier: 'A',
    accent: '#E82127',
    members: [
      'elon-musk',
      'jb-straubel',
      'franz-von-holzhausen',
      'drew-baglino',
      'andrej-karpathy',
    ],
  },
  {
    id: 'uber-2015',
    name: 'UBER',
    era: 'HYPERGROWTH · 2014–16',
    tier: 'A',
    accent: '#D6D6D6',
    members: ['travis-kalanick', 'garrett-camp', 'ryan-graves', 'emil-michael', 'thuan-pham'],
  },
  {
    id: 'twitter-2006',
    name: 'TWITTER',
    era: 'FOUNDING ERA · 2006–10',
    tier: 'A',
    accent: '#4FC3F7',
    members: ['jack-dorsey', 'ev-williams', 'biz-stone', 'noah-glass', 'jason-goldman'],
  },
  {
    id: 'airbnb-2010s',
    name: 'AIRBNB',
    era: 'BREAKOUT · 2010s',
    tier: 'A',
    accent: '#FF5A5F',
    members: [
      'brian-chesky',
      'joe-gebbia',
      'nathan-blecharczyk',
      'belinda-johnson',
      'jonathan-mildenhall',
    ],
  },
  {
    id: 'deepmind-2018',
    name: 'DEEPMIND',
    era: 'AI LAB · 2016–20',
    tier: 'A',
    accent: '#8AB4F8',
    members: [
      'demis-hassabis',
      'shane-legg',
      'mustafa-suleyman',
      'david-silver',
      'oriol-vinyals',
    ],
  },
  {
    id: 'wiz-2020s',
    name: 'WIZ',
    era: 'FOUNDING TEAM · 2020s',
    tier: 'A',
    accent: '#5BE3B0',
    members: ['assaf-rappaport', 'ami-luttwak', 'yinon-costica', 'roy-reznik'],
  },
  {
    id: 'monday-2010s',
    name: 'MONDAY.COM',
    era: '2010s–20s',
    tier: 'A',
    accent: '#FF3D57',
    members: ['roy-mann', 'eran-zinman', 'daniel-lereya'],
  },
  {
    id: 'lemonade-2015',
    name: 'LEMONADE',
    era: '2015–20s',
    tier: 'A',
    accent: '#FF4FA0',
    members: ['daniel-schreiber', 'shai-wininger', 'ty-sagalow'],
  },
  {
    id: 'mobileye-2000s',
    name: 'MOBILEYE',
    era: '2000s–10s',
    tier: 'A',
    accent: '#38C6C6',
    members: ['amnon-shashua', 'ziv-aviram', 'gaby-hayon'],
  },
  {
    id: 'fiverr-2010s',
    name: 'FIVERR',
    era: '2010s',
    tier: 'A',
    accent: '#8BE04E',
    members: ['micha-kaufman', 'shai-wininger', 'gali-arnon'],
  },
  {
    id: 'check-point-1990s',
    name: 'CHECK POINT',
    era: '1990s',
    tier: 'A',
    accent: '#C08BFF',
    members: ['gil-shwed', 'marius-nacht', 'shlomo-kramer', 'dorit-dor'],
  },

  // ── Tier B · themed all-star draws ──────────────────────────────────────
  {
    id: 'saas-2018',
    name: 'SAAS FOUNDERS',
    era: '2018',
    tier: 'B',
    accent: '#7C9BFF',
    members: [
      'patrick-collison',
      'tobi-lutke',
      'stewart-butterfield',
      'drew-houston',
      'aaron-levie',
      'melanie-perkins',
    ],
  },
  {
    id: 'product-2020',
    name: 'PRODUCT FOUNDERS',
    era: '2020',
    tier: 'B',
    accent: '#5BE3B0',
    members: ['dylan-field', 'brian-chesky', 'ivan-zhao', 'stewart-butterfield', 'vlad-magdalin'],
  },
  {
    id: 'ai-founders-2024',
    name: 'AI FOUNDERS',
    era: '2024',
    tier: 'B',
    accent: '#C08BFF',
    members: ['sam-altman', 'dario-amodei', 'demis-hassabis', 'elon-musk', 'aravind-srinivas'],
  },
  {
    id: 'ai-researchers-2017',
    name: 'AI RESEARCHERS',
    era: '2017',
    tier: 'B',
    accent: '#8AB4F8',
    members: [
      'geoffrey-hinton',
      'yann-lecun',
      'yoshua-bengio',
      'ilya-sutskever',
      'demis-hassabis',
      'andrej-karpathy',
    ],
  },
  {
    id: 'crypto-2021',
    name: 'CRYPTO',
    era: '2021',
    tier: 'B',
    accent: '#F2CB4E',
    members: [
      'sam-bankman-fried',
      'brian-armstrong',
      'changpeng-zhao',
      'vitalik-buterin',
      'anatoly-yakovenko',
      'do-kwon',
    ],
  },
  {
    id: 'consumer-social-2012',
    name: 'CONSUMER SOCIAL',
    era: '2012',
    tier: 'B',
    accent: '#4C8DFF',
    members: [
      'mark-zuckerberg',
      'jack-dorsey',
      'kevin-systrom',
      'evan-spiegel',
      'ben-silbermann',
    ],
  },
  {
    id: 'devtools-2023',
    name: 'DEVELOPER TOOLS',
    era: '2023',
    tier: 'B',
    accent: '#5BC8FF',
    members: [
      'dylan-field',
      'guillermo-rauch',
      'mitchell-hashimoto',
      'nat-friedman',
      'sid-sijbrandij',
    ],
  },
  {
    id: 'cloud-2010s',
    name: 'CLOUD ERA',
    era: '2010s',
    tier: 'B',
    accent: '#FFA94D',
    members: ['jeff-bezos', 'andy-jassy', 'satya-nadella', 'urs-holzle', 'werner-vogels'],
  },
  {
    id: 'design-gods',
    name: 'DESIGN GODS',
    era: 'ALL ERAS',
    tier: 'B',
    accent: '#E8E8ED',
    members: ['jony-ive', 'dylan-field', 'brian-chesky', 'tony-fadell', 'steve-jobs', 'joe-gebbia'],
  },
  {
    id: 'growth-gods',
    name: 'GROWTH GODS',
    era: 'ALL ERAS',
    tier: 'B',
    accent: '#8BE04E',
    members: [
      'sheryl-sandberg',
      'andrew-chen',
      'chamath-palihapitiya',
      'naomi-gleit',
      'javier-olivan',
      'steve-case',
    ],
  },
  {
    id: 'enterprise-founders',
    name: 'ENTERPRISE FOUNDERS',
    era: 'ALL ERAS',
    tier: 'B',
    accent: '#4C8DFF',
    members: ['marc-benioff', 'larry-ellison', 'frank-slootman', 'aaron-levie', 'parker-conrad'],
  },
  {
    id: 'commerce-founders',
    name: 'COMMERCE FOUNDERS',
    era: 'ALL ERAS',
    tier: 'B',
    accent: '#FFA94D',
    members: ['jeff-bezos', 'tobi-lutke', 'patrick-collison', 'jack-ma', 'pierre-omidyar'],
  },
  {
    id: 'marketplace-founders',
    name: 'MARKETPLACE FOUNDERS',
    era: 'ALL ERAS',
    tier: 'B',
    accent: '#FF5A5F',
    members: ['brian-chesky', 'travis-kalanick', 'garrett-camp', 'logan-green', 'tony-xu'],
  },
  {
    id: 'hardware-builders',
    name: 'HARDWARE BUILDERS',
    era: 'ALL ERAS',
    tier: 'B',
    accent: '#D6D6D6',
    members: [
      'steve-jobs',
      'jensen-huang',
      'elon-musk',
      'tony-fadell',
      'palmer-luckey',
      'lisa-su',
      'jim-keller',
    ],
  },
  {
    id: 'infra-builders',
    name: 'INFRASTRUCTURE BUILDERS',
    era: 'ALL ERAS',
    tier: 'B',
    accent: '#38C6C6',
    members: [
      'jensen-huang',
      'werner-vogels',
      'mitchell-hashimoto',
      'urs-holzle',
      'jeff-dean',
      'morris-chang',
    ],
  },
  {
    id: 'creator-economy-2021',
    name: 'CREATOR ECONOMY',
    era: '2021',
    tier: 'B',
    accent: '#FF4FA0',
    members: ['daniel-ek', 'ben-silbermann', 'kevin-systrom', 'evan-spiegel', 'biz-stone'],
  },
  {
    id: 'israeli-cyber',
    name: 'ISRAELI CYBER MAFIA',
    era: 'ALL ERAS',
    tier: 'B',
    accent: '#C08BFF',
    members: [
      'gil-shwed',
      'assaf-rappaport',
      'shlomo-kramer',
      'tomer-weingarten',
      'udi-mokady',
      'mickey-boodaei',
    ],
  },

  // ── Tier C · chaos draws ────────────────────────────────────────────────
  {
    id: 'villains',
    name: 'TECH VILLAINS',
    era: 'ALL ERAS',
    tier: 'C',
    accent: '#FF3B3B',
    members: [
      'elizabeth-holmes',
      'sam-bankman-fried',
      'adam-neumann',
      'travis-kalanick',
      'do-kwon',
      'martin-shkreli',
      'trevor-milton',
    ],
  },
  {
    id: 'chaos-ceos',
    name: 'CHAOS CEOS',
    era: 'ALL ERAS',
    tier: 'C',
    accent: '#FF7A3D',
    members: ['elon-musk', 'adam-neumann', 'travis-kalanick', 'steve-jobs', 'sam-bankman-fried'],
  },
  {
    id: 'storytellers',
    name: 'GREATEST STORYTELLERS',
    era: 'ALL ERAS',
    tier: 'C',
    accent: '#F2CB4E',
    members: ['steve-jobs', 'adam-neumann', 'elon-musk', 'elizabeth-holmes', 'marc-benioff'],
  },
  {
    id: 'fundraising-monsters',
    name: 'FUNDRAISING MONSTERS',
    era: 'ALL ERAS',
    tier: 'C',
    accent: '#F2CB4E',
    members: [
      'adam-neumann',
      'sam-altman',
      'elon-musk',
      'elizabeth-holmes',
      'sam-bankman-fried',
      'masayoshi-son',
    ],
  },
  {
    id: 'founder-mode',
    name: 'FOUNDER MODE',
    era: 'ALL ERAS',
    tier: 'C',
    accent: '#8BE04E',
    members: [
      'steve-jobs',
      'mark-zuckerberg',
      'jeff-bezos',
      'elon-musk',
      'jensen-huang',
      'brian-chesky',
    ],
  },
  {
    id: 'board-nightmare',
    name: 'BOARD NIGHTMARE',
    era: 'ALL ERAS',
    tier: 'C',
    accent: '#FF5C5C',
    members: ['steve-jobs', 'elon-musk', 'adam-neumann', 'travis-kalanick', 'sam-bankman-fried'],
  },
  {
    id: 'almost-changed-the-world',
    name: 'ALMOST CHANGED THE WORLD',
    era: 'ALL ERAS',
    tier: 'C',
    accent: '#FF8FA3',
    members: [
      'elizabeth-holmes',
      'adam-neumann',
      'palmer-luckey',
      'travis-kalanick',
      'trevor-milton',
      'emad-mostaque',
    ],
  },
  {
    id: 'implosions',
    name: 'SPECTACULAR IMPLOSIONS',
    era: 'ALL ERAS',
    tier: 'C',
    accent: '#FF3B3B',
    members: [
      'sam-bankman-fried',
      'elizabeth-holmes',
      'adam-neumann',
      'meg-whitman',
      'do-kwon',
      'su-zhu',
    ],
  },
  {
    id: 'dotcom-mania',
    name: 'DOT-COM MANIA',
    era: 'ALL ERAS',
    tier: 'C',
    accent: '#FF8FA3',
    members: ['jeff-bezos', 'mark-cuban', 'jerry-yang', 'pierre-omidyar', 'marc-benioff', 'steve-case'],
  },
  {
    id: 'crypto-casino',
    name: 'CRYPTO CASINO',
    era: 'ALL ERAS',
    tier: 'C',
    accent: '#F2CB4E',
    members: [
      'sam-bankman-fried',
      'changpeng-zhao',
      'do-kwon',
      'brian-armstrong',
      'vitalik-buterin',
      'su-zhu',
    ],
  },
  {
    id: 'vision-over-reality',
    name: 'VISION > REALITY',
    era: 'ALL ERAS',
    tier: 'C',
    accent: '#C08BFF',
    members: [
      'elizabeth-holmes',
      'adam-neumann',
      'elon-musk',
      'masayoshi-son',
      'trevor-milton',
      'emad-mostaque',
    ],
  },
  {
    id: 'zero-governance',
    name: 'ZERO GOVERNANCE',
    era: 'ALL ERAS',
    tier: 'C',
    accent: '#FF7A3D',
    members: [
      'sam-bankman-fried',
      'changpeng-zhao',
      'adam-neumann',
      'travis-kalanick',
      'martin-shkreli',
      'do-kwon',
    ],
  },
  {
    id: 'hundred-million',
    name: 'WOULD YOU GIVE THEM $100M?',
    era: 'ALL ERAS',
    tier: 'C',
    accent: '#F2CB4E',
    members: [
      'sam-altman',
      'adam-neumann',
      'elizabeth-holmes',
      'jensen-huang',
      'chamath-palihapitiya',
      'palmer-luckey',
    ],
  },

  // ── Historical · a whole year, drafted ──────────────────────────────────
  {
    id: 'dotcom-1999',
    name: 'DOT-COM',
    era: '1999',
    tier: 'historical',
    accent: '#FF8FA3',
    members: [
      'jeff-bezos',
      'bill-gates',
      'steve-jobs',
      'jerry-yang',
      'pierre-omidyar',
      'marc-andreessen',
    ],
  },
  {
    id: 'web2-2007',
    name: 'WEB 2.0',
    era: '2007',
    tier: 'historical',
    accent: '#4C8DFF',
    members: [
      'steve-jobs',
      'mark-zuckerberg',
      'jeff-bezos',
      'larry-page',
      'jack-dorsey',
      'marc-benioff',
    ],
  },
  {
    id: 'mobile-2012',
    name: 'MOBILE',
    era: '2012',
    tier: 'historical',
    accent: '#5BE3B0',
    members: [
      'mark-zuckerberg',
      'kevin-systrom',
      'evan-spiegel',
      'travis-kalanick',
      'brian-chesky',
      'jack-dorsey',
    ],
  },
  {
    id: 'unicorn-2016',
    name: 'UNICORN ERA',
    era: '2016',
    tier: 'historical',
    accent: '#C08BFF',
    members: [
      'mark-zuckerberg',
      'travis-kalanick',
      'brian-chesky',
      'patrick-collison',
      'evan-spiegel',
      'stewart-butterfield',
    ],
  },
  {
    id: 'everything-bubble-2021',
    name: 'EVERYTHING BUBBLE',
    era: '2021',
    tier: 'historical',
    accent: '#F2CB4E',
    members: [
      'elon-musk',
      'sam-bankman-fried',
      'adam-neumann',
      'brian-armstrong',
      'changpeng-zhao',
      'sam-altman',
      'masayoshi-son',
    ],
  },
  {
    id: 'ai-explosion-2023',
    name: 'AI EXPLOSION',
    era: '2023',
    tier: 'historical',
    accent: '#8BE04E',
    members: [
      'jensen-huang',
      'sam-altman',
      'satya-nadella',
      'demis-hassabis',
      'dario-amodei',
      'mark-zuckerberg',
    ],
  },
];

export const getCohort = (id: string): Cohort => {
  const cohort = COHORTS.find((c) => c.id === id);
  if (!cohort) throw new Error(`Unknown cohort: ${id}`);
  return cohort;
};

export const cohortsByTier = (tier: Cohort['tier']): Cohort[] =>
  COHORTS.filter((cohort) => cohort.tier === tier);
